/**
 * PublicModeToggle — header IconButton that toggles panel-wide
 * privacy ("public") mode.
 *
 * Use case: the user is presenting their screen, sitting in a meeting,
 * or otherwise has eyes-on-screen they don't trust with whatever might
 * arrive next. Tapping the toggle collapses the list to one inert card
 * per app (app icon + name + aggregate count, no content or actions) and
 * hides the search box + group/timeline toggle; tapping again restores
 * the full list. See the privacy-mode spec for the collapse behavior.
 *
 * Headless: no `browser.*`, no storage, no i18n helpers. Hosts pass
 * `active` (current state) and `onToggle` (the host owns the state
 * machine — `useState` at the app root for V1, future cross-tab sync
 * via `ReflectoStorage.publicMode` is wired but explicitly NOT used
 * for V1 because the spec demands public-mode reset to OFF on every
 * page/SW restart). The `label` prop becomes the IconButton's
 * accessible name.
 *
 * Icon mapping mirrors the Show/Hide affordance other apps converge on:
 *   - inactive (publicMode OFF) → `Eye` glyph: "tap to enable hide"
 *   - active   (publicMode ON)  → `EyeOff` glyph: "tap to disable hide"
 *
 * `aria-pressed={active}` exposes the toggle state to assistive tech
 * (the IconButton primitive forwards arbitrary `aria-*` props through
 * via its `Omit<…, "aria-label" | "className" | "children">` spread,
 * so this composes without leaking other DOM-level concerns).
 */
import { Eye, EyeOff } from "@untitledui/icons";
import { IconButton } from "../IconButton";

export interface PublicModeToggleProps {
  /** Current public-mode state. `true` = privacy mode on (collapsed per-app cards). */
  active: boolean;
  /** Called on every click. Host flips its own state. */
  onToggle: () => void;
  /** Localized accessible label for the IconButton. */
  label: string;
}

export function PublicModeToggle({ active, onToggle, label }: PublicModeToggleProps) {
  return (
    <IconButton
      icon={active ? EyeOff : Eye}
      label={label}
      variant="default"
      size="m"
      onClick={onToggle}
      aria-pressed={active}
    />
  );
}
